import { Avatar, Button, phone as phoneLib } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { useTranslation, LTR_TEXT } from '../../i18n';
import MobileCard, { MobileCardHead, MobileCardMeta, MobileCardActions } from '../MobileCard';
import MobileMoneyRow from '../MobileMoneyRow';

/**
 * One invoice, as a card.
 *
 * The desk reads this record as a seven-column row. At 360px that row forces the
 * PAGE to scroll sideways, so the phone keeps the four facts a cashier acts on —
 * who, for which doctor, what was billed, and what is still owed — and drops the
 * rest into the settle sheet a tap away.
 *
 * **Render-only, and it derives nothing.** `paid`, `due` and `status` arrive
 * already computed from `lib/invoice.js` and `lib/billing.js`, because "what
 * does this invoice owe" must have exactly one answer in this app and a card is
 * not the place it is decided.
 *
 * The three money rows are stacked full width rather than sitting in a KPI grid:
 * a quarter of 320px cannot hold «۱۲٬۴۵۰٬۰۰۰», so the figure would either wrap
 * mid-number or take the page sideways.
 */
export default function MobileInvoiceCard({
  invoice,
  /** Σ of the payments recorded against it, in RIALS. */
  paid = 0,
  /** What is still owed, in RIALS. */
  due = 0,
  /** `{ label, bg, text }` from `STATUS_META` — the money-derived status, not the wire one. */
  statusMeta,
  /** True when the invoice can still be settled (neither PAID nor VOID). */
  canSettle = false,
  onOpen,
  onSettle,
  'data-testid': testId,
}) {
  const { t, moneyUnit, formatMoney, formatList } = useTranslation();

  const name = invoice.patientName
    || t('patients.placeholder.byId', { id: invoice.patientId?.slice(-6) || '' });
  const descriptions = (invoice.lineItems || []).map((li) => li.description).filter(Boolean);

  return (
    <MobileCard
      // Tapping the body opens the invoice, exactly as clicking the desktop's
      // row does. This is safe to pair with the action button below because
      // `MobileCard`'s tap target is a sibling overlay rather than a wrapper —
      // the two controls sit side by side in the DOM instead of nesting, which
      // is what would have been invalid.
      onClick={onOpen ? () => onOpen(invoice) : undefined}
      label={t('cashier.mobile.openInvoiceFor', { name })}
      marker={due > 0 ? C.red : C.green}
      data-testid={testId}
      data-state={canSettle ? 'open' : 'closed'}
    >
      <MobileCardHead>
        <Avatar name={name} size={36} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            dir="auto"
            style={{
              display: 'block', fontSize: '0.875rem', fontWeight: 700, color: C.textPrimary,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {name}
          </span>
          {invoice.doctorName ? (
            <span style={{ display: 'block', fontSize: '0.6875rem', color: C.textSecondary }}>
              {t('doctors.placeholder.withTitle', { name: invoice.doctorName })}
            </span>
          ) : null}
        </span>
        {statusMeta ? (
          <span
            style={{
              flexShrink: 0, fontSize: '0.625rem', fontWeight: 700,
              padding: '0.1875rem 0.625rem', borderRadius: 999,
              background: statusMeta.bg, color: statusMeta.text, whiteSpace: 'nowrap',
            }}
            data-testid={testId ? `${testId}-status` : undefined}
          >
            {statusMeta.label}
          </span>
        ) : null}
      </MobileCardHead>

      {/* The billed work, joined. A missing part is omitted rather than rendered
          as «—»: an em dash in a list of services reads as a service. */}
      <MobileCardMeta
        parts={[
          descriptions.length ? formatList(descriptions) : t('accounting.empty.noLineItems'),
          invoice.patientPhone
            ? <span key="phone" {...LTR_TEXT}>{phoneLib.forDisplay(invoice.patientPhone)}</span>
            : null,
        ]}
      />

      <div style={{ display: 'grid', gap: '0.125rem', marginTop: '0.5rem' }}>
        <MobileMoneyRow
          label={t('cashier.invoice.total')}
          value={formatMoney(invoice.patientPayable, { currency: invoice?.currency })}
          unit={moneyUnit('short', invoice?.currency)}
          data-testid={testId ? `${testId}-total` : undefined}
        />
        <MobileMoneyRow
          label={t('accounting.fields.paid')}
          value={formatMoney(paid)}
          unit={moneyUnit('short')}
          tone={paid > 0 ? 'good' : 'muted'}
          data-testid={testId ? `${testId}-paid` : undefined}
        />
        <MobileMoneyRow
          label={t('accounting.fields.remaining')}
          value={formatMoney(due)}
          unit={moneyUnit('short')}
          tone={due > 0 ? 'danger' : 'good'}
          strong
          data-testid={testId ? `${testId}-due` : undefined}
        />
      </div>

      {/* ONE full-width action, thumb-sized, and the card body around it is not
          itself a control: a card that is a <button> cannot legally contain one,
          and a settled invoice still has to be openable as a receipt. «تسویه» and
          «مشاهده» open the same sheet — exactly as the desktop's row click and its
          «تسویه» button both open the same drawer — so the label only says what
          the operator is going there to do. */}
      <MobileCardActions>
        <Button
          variant={canSettle ? 'default' : 'outline'}
          onClick={() => (canSettle && onSettle ? onSettle(invoice) : onOpen?.(invoice))}
          aria-label={t('cashier.mobile.openInvoice', { name })}
          style={{ flex: 1, minHeight: '2.75rem' }}
          data-testid={testId ? `${testId}-${canSettle ? 'settle' : 'view'}` : undefined}
        >
          {canSettle ? t('accounting.actions.settle') : t('common.actions.view')}
        </Button>
      </MobileCardActions>
    </MobileCard>
  );
}
